// Pure, dependency-free playlist logic shared by the Worker and the frontend.
// Everything here is unit-tested in test/playlist.test.ts.

import {
  BrandingConfig,
  CaptionMode,
  ClockOverlayConfig,
  DEFAULT_SETTINGS,
  FitMode,
  LogoCorner,
  PlayableSlide,
  PlaylistSettings,
  Rotation,
  Slide,
  SlideSchedule,
  WorkspaceConfig,
} from "./types";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export function normalizeSchedule(raw: unknown): SlideSchedule | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  const out: SlideSchedule = {};
  if (typeof r.startDate === "string" && DATE_RE.test(r.startDate)) out.startDate = r.startDate;
  if (typeof r.endDate === "string" && DATE_RE.test(r.endDate)) out.endDate = r.endDate;
  if (Array.isArray(r.days)) {
    const days = [
      ...new Set(r.days.filter((d): d is number => typeof d === "number" && d >= 0 && d <= 6)),
    ].sort((a, b) => a - b);
    if (days.length && days.length < 7) out.days = days;
  }
  if (typeof r.startTime === "string" && TIME_RE.test(r.startTime)) out.startTime = r.startTime;
  if (typeof r.endTime === "string" && TIME_RE.test(r.endTime)) out.endTime = r.endTime;
  return Object.keys(out).length ? out : undefined;
}

/** Whether a slide's schedule allows it to play at `now` (viewer-local time). */
export function isScheduledNow(schedule: SlideSchedule | undefined, now: Date): boolean {
  if (!schedule) return true;
  const pad = (n: number) => n.toString().padStart(2, "0");
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  if (schedule.startDate && today < schedule.startDate) return false;
  if (schedule.endDate && today > schedule.endDate) return false;
  if (schedule.days && schedule.days.length && !schedule.days.includes(now.getDay())) return false;
  if (schedule.startTime || schedule.endTime) {
    const cur = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const start = schedule.startTime ?? "00:00";
    const end = schedule.endTime ?? "23:59";
    if (start <= end) {
      if (cur < start || cur > end) return false;
    } else if (cur < start && cur > end) {
      return false; // outside an overnight window
    }
  }
  return true;
}

export const FIT_MODES: FitMode[] = ["cover", "contain", "fill"];
export const ROTATIONS: Rotation[] = [0, 90, 180, 270];

const MIN_DURATION = 1;
const MAX_DURATION = 3600;
const MIN_IDLE = 5;
const MAX_IDLE = 3600;
const MIN_CROSSFADE = 0;
const MAX_CROSSFADE = 10000;

function clamp(value: number, min: number, max: number, fallback: number): number {
  if (typeof value !== "number" || Number.isNaN(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
}

function asFit(value: unknown): FitMode {
  return FIT_MODES.includes(value as FitMode) ? (value as FitMode) : "cover";
}

function asRotation(value: unknown): Rotation {
  return ROTATIONS.includes(value as Rotation) ? (value as Rotation) : 0;
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asBool(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

export function normalizeCaptions(value: unknown): CaptionMode {
  if (value === "off" || value === "auto") return value;
  if (value && typeof value === "object" && typeof (value as any).vttKey === "string") {
    const key = (value as any).vttKey.trim();
    if (key) return { vttKey: key };
  }
  return "off";
}

/** Validate and coerce a single slide coming from the client/storage into a
 *  well-formed Slide. Returns null for unrecognized slides so callers can drop
 *  them. */
export function normalizeSlide(raw: unknown, defaultDuration: number): Slide | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const id = asString(r.id).trim() || crypto.randomUUID();
  const enabled = asBool(r.enabled, true);
  const durationSec = clamp(r.durationSec as number, MIN_DURATION, MAX_DURATION, defaultDuration);
  const group = asString(r.group).trim().slice(0, 60) || undefined;
  const schedule = normalizeSchedule(r.schedule);
  const hideLogo = asBool(r.hideLogo, false) || undefined;

  if (r.type === "groupref") {
    const sharedGroupId = asString(r.sharedGroupId).trim();
    if (!sharedGroupId) return null;
    return {
      id,
      type: "groupref",
      enabled,
      sharedGroupId,
      name: asString(r.name) || undefined,
      schedule,
      hideLogo,
    };
  }

  if (r.type === "clock") {
    return {
      id,
      type: "clock",
      enabled,
      durationSec,
      group,
      schedule,
      hideLogo,
      showDate: asBool(r.showDate, true),
      showSeconds: asBool(r.showSeconds, false),
      hour24: asBool(r.hour24, false),
      showWeather: asBool(r.showWeather, false),
      locationName: asString(r.locationName) || undefined,
      latitude: typeof r.latitude === "number" ? r.latitude : undefined,
      longitude: typeof r.longitude === "number" ? r.longitude : undefined,
      tempUnit: r.tempUnit === "C" ? "C" : "F",
      bgColor: asString(r.bgColor, "#0b1120"),
      textColor: asString(r.textColor, "#f8fafc"),
    };
  }

  if (r.type === "youtube") {
    const videoId = asString(r.videoId).trim();
    if (!videoId) return null;
    return {
      id,
      type: "youtube",
      enabled,
      durationSec,
      group,
      schedule,
      hideLogo,
      videoId,
      title: asString(r.title) || undefined,
      loop: asBool(r.loop, false),
    };
  }

  if (r.type === "vimeo") {
    const videoId = asString(r.videoId).trim();
    if (!videoId) return null;
    return {
      id,
      type: "vimeo",
      enabled,
      durationSec,
      group,
      schedule,
      hideLogo,
      videoId,
      hash: asString(r.hash).trim() || undefined,
      title: asString(r.title) || undefined,
      loop: asBool(r.loop, false),
    };
  }

  if (r.type === "team") {
    return {
      id,
      type: "team",
      enabled,
      durationSec,
      group,
      schedule,
      hideLogo,
      photoKey: asString(r.photoKey).trim() || undefined,
      name: asString(r.name),
      role: asString(r.role),
      bio: asString(r.bio),
      bgColor: asString(r.bgColor, "#0b1120"),
      textColor: asString(r.textColor, "#f8fafc"),
    };
  }

  if (r.type === "qr") {
    const data = asString(r.data).trim();
    if (!data) return null;
    return {
      id,
      type: "qr",
      enabled,
      durationSec,
      group,
      schedule,
      hideLogo,
      data,
      title: asString(r.title) || undefined,
      caption: asString(r.caption) || undefined,
      bgColor: asString(r.bgColor, "#ffffff"),
      fgColor: asString(r.fgColor, "#0b1120"),
    };
  }

  if (r.type === "review") {
    const ratingRaw = typeof r.rating === "number" ? Math.round(r.rating) : 5;
    const source = r.source === "google" || r.source === "yelp" ? r.source : "other";
    return {
      id,
      type: "review",
      enabled,
      durationSec,
      group,
      schedule,
      hideLogo,
      quote: asString(r.quote),
      author: asString(r.author),
      rating: Math.min(5, Math.max(1, ratingRaw)),
      source,
      bgColor: asString(r.bgColor, "#0b1120"),
      textColor: asString(r.textColor, "#f8fafc"),
    };
  }

  if (r.type === "text") {
    return {
      id,
      type: "text",
      enabled,
      durationSec,
      group,
      schedule,
      hideLogo,
      title: asString(r.title),
      body: asString(r.body),
      bgColor: asString(r.bgColor, "#0b1120"),
      textColor: asString(r.textColor, "#f8fafc"),
      align: r.align === "left" || r.align === "right" ? r.align : "center",
    };
  }

  const mediaKey = asString(r.mediaKey).trim();
  if (!mediaKey) return null;
  const fileName = asString(r.fileName) || undefined;

  if (r.type === "video") {
    return {
      id,
      type: "video",
      enabled,
      durationSec,
      group,
      schedule,
      hideLogo,
      mediaKey,
      fileName,
      fit: asFit(r.fit),
      rotation: asRotation(r.rotation),
      audio: asBool(r.audio, false),
      loop: asBool(r.loop, false),
      captions: normalizeCaptions(r.captions),
    };
  }

  if (r.type === "image") {
    return {
      id,
      type: "image",
      enabled,
      durationSec,
      group,
      schedule,
      hideLogo,
      mediaKey,
      fileName,
      fit: asFit(r.fit),
      rotation: asRotation(r.rotation),
      kenBurns: asBool(r.kenBurns, false) || undefined,
    };
  }

  return null;
}

function normalizeClockOverlay(raw: unknown): ClockOverlayConfig {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    enabled: asBool(r.enabled, false),
    showDate: asBool(r.showDate, false),
    showSeconds: asBool(r.showSeconds, false),
    hour24: asBool(r.hour24, false),
    showWeather: asBool(r.showWeather, false),
    locationName: asString(r.locationName) || undefined,
    latitude: typeof r.latitude === "number" ? r.latitude : undefined,
    longitude: typeof r.longitude === "number" ? r.longitude : undefined,
    tempUnit: r.tempUnit === "C" ? "C" : "F",
  };
}

const LOGO_CORNERS: LogoCorner[] = ["top-left", "top-right", "bottom-left", "bottom-right"];

function normalizeBranding(raw: unknown): BrandingConfig {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const corner = LOGO_CORNERS.includes(r.logoCorner as LogoCorner)
    ? (r.logoCorner as LogoCorner)
    : "top-left";
  return {
    logoKey: asString(r.logoKey).trim() || undefined,
    logoCorner: corner,
    logoSizePct: clamp(r.logoSizePct as number, 3, 40, 12),
    logoOpacity: clamp(r.logoOpacity as number, 10, 100, 90),
    useBrandTextColors: asBool(r.useBrandTextColors, false),
    brandBgColor: asString(r.brandBgColor, "#0b1120"),
    brandTextColor: asString(r.brandTextColor, "#f8fafc"),
  };
}

export function normalizeSettings(raw: unknown): PlaylistSettings {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    idleTimeoutSec: clamp(r.idleTimeoutSec as number, MIN_IDLE, MAX_IDLE, DEFAULT_SETTINGS.idleTimeoutSec),
    crossfadeMs: clamp(r.crossfadeMs as number, MIN_CROSSFADE, MAX_CROSSFADE, DEFAULT_SETTINGS.crossfadeMs),
    defaultDurationSec: clamp(
      r.defaultDurationSec as number,
      MIN_DURATION,
      MAX_DURATION,
      DEFAULT_SETTINGS.defaultDurationSec,
    ),
    shuffle: asBool(r.shuffle, DEFAULT_SETTINGS.shuffle),
    clockOverlay: normalizeClockOverlay(r.clockOverlay),
    branding: normalizeBranding(r.branding),
  };
}

/** Validate/coerce an entire config payload. Invalid slides are dropped. */
export function normalizeConfig(raw: unknown): WorkspaceConfig {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const settings = normalizeSettings(r.settings);
  const slidesRaw = Array.isArray(r.slides) ? r.slides : [];
  const slides = slidesRaw
    .map((s) => normalizeSlide(s, settings.defaultDurationSec))
    .filter((s): s is Slide => s !== null);
  return { settings, slides };
}

/** The slides that should actually play (enabled, fully resolved). Group
 *  references are skipped defensively — they should already be expanded. */
export function playableSlides(config: WorkspaceConfig): PlayableSlide[] {
  return config.slides.filter(
    (s): s is PlayableSlide => s.enabled && s.type !== "groupref",
  );
}

/** Normalize a list of slides for a Shared Group (group references are not
 *  allowed inside a shared group, to avoid nesting). */
export function normalizeReusableSlides(raw: unknown, defaultDuration = 8): Slide[] {
  const arr = Array.isArray(raw) ? raw : [];
  return arr
    .map((s) => normalizeSlide(s, defaultDuration))
    .filter((s): s is Slide => s !== null && s.type !== "groupref");
}

/**
 * Replace each enabled group-reference slide with the linked shared group's
 * slides, tagged with a shared group label so they stay together (and in
 * order) when the playlist is shuffled. `groupSlides` maps a shared group id to
 * its current slides.
 */
export function expandSlides(slides: Slide[], groupSlides: Record<string, Slide[]>): Slide[] {
  const out: Slide[] = [];
  for (const slide of slides) {
    if (slide.type === "groupref") {
      if (!slide.enabled) continue;
      const inner = groupSlides[slide.sharedGroupId] ?? [];
      for (const s of inner) {
        if (s.type === "groupref") continue; // no nested references
        out.push({
          ...s,
          // Prefix with the reference's own id: the same group linked twice
          // must not produce duplicate slide ids (they become React keys).
          id: `${slide.id}:${s.id}`,
          group: `sg:${slide.sharedGroupId}`,
          // A scheduled group reference applies its schedule to every slide.
          ...(slide.schedule ? { schedule: slide.schedule } : {}),
          ...(slide.hideLogo ? { hideLogo: true } : {}),
        });
      }
    } else {
      out.push(slide);
    }
  }
  return out;
}

/** Compute the next index in a wrapping playlist. */
export function nextIndex(current: number, length: number): number {
  if (length <= 0) return 0;
  return (current + 1) % length;
}

/** Deterministic shuffle (Fisher–Yates) seeded by `seed` so playback order can
 *  be reproduced and tested. */
export function shuffleOrder(length: number, seed: number): number[] {
  const order = Array.from({ length }, (_, i) => i);
  let state = (seed >>> 0) || 1;
  const rand = () => {
    // xorshift32
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return ((state >>> 0) % 1_000_000) / 1_000_000;
  };
  for (let i = length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order;
}

/**
 * Build the playback order for the given slides.
 *
 * - When `shuffle` is false, slides play in their listed order.
 * - When `shuffle` is true, slides are shuffled — but slides that share a
 *   non-empty `group` are treated as a single block: they stay together and in
 *   their listed order, and only the blocks (and ungrouped slides) are shuffled
 *   relative to one another.
 *
 * Returns an array of indices into `slides`.
 */
export function playOrder(slides: Slide[], shuffle: boolean, seed: number): number[] {
  if (!shuffle) return slides.map((_, i) => i);

  // Group consecutive/scattered slides by their group label, preserving the
  // order in which their members appear in the list.
  const units: number[][] = [];
  const groupUnit = new Map<string, number>();
  slides.forEach((slide, i) => {
    const g = (slide.type === "groupref" ? "" : slide.group ?? "").trim();
    if (g) {
      const existing = groupUnit.get(g);
      if (existing !== undefined) {
        units[existing].push(i);
        return;
      }
      groupUnit.set(g, units.length);
    }
    units.push([i]);
  });

  const shuffledUnits = shuffleOrder(units.length, seed);
  const order: number[] = [];
  for (const u of shuffledUnits) order.push(...units[u]);
  return order;
}

/**
 * Collect the R2 media keys referenced by a (display) config: image/video
 * files, video caption tracks, and the clinic logo. Used to pre-cache media
 * for offline playback.
 */
export function collectMediaKeys(config: WorkspaceConfig): string[] {
  const keys: string[] = [];
  for (const slide of config.slides) {
    if (slide.type === "image" || slide.type === "video") keys.push(slide.mediaKey);
    if (slide.type === "video" && typeof slide.captions === "object") keys.push(slide.captions.vttKey);
    if (slide.type === "team" && slide.photoKey) keys.push(slide.photoKey);
  }
  if (config.settings.branding?.logoKey) keys.push(config.settings.branding.logoKey);
  return [...new Set(keys.filter(Boolean))];
}

/** CSS transform string for a slide's rotation. */
export function rotationTransform(rotation: Rotation): string {
  return rotation === 0 ? "none" : `rotate(${rotation}deg)`;
}

/** Map a fit mode to the CSS object-fit value. */
export function fitToObjectFit(fit: FitMode): "cover" | "contain" | "fill" {
  return fit;
}
